/**
 * Payment Prompt Component
 *
 * Displays when user is authenticated but doesn't have an active subscription.
 * Provides interface to set up payment method and subscription.
 */

import React from 'react';
import { createLogger } from '@ctrl-enter/tools/utils';

const logger = createLogger('client:component:payment-prompt');
import { Button } from './button';
import { Card } from './card';
import { cn } from '../utils/cn';
import { usePlatform } from '../contexts/platform-context';
import BlueRocketSvg from '../assets/icons/blue-rocket.svg';
import GreenCheckSvg from '../assets/icons/green-check.svg';
import { useAuth } from '../services/auth-service';

interface PaymentPromptProps {
  onAddPayment?: () => Promise<void> | void;
  onClose?: () => void;
  isLoading?: boolean;
  className?: string;
  isWarning?: boolean;
}

export const PaymentPrompt: React.FC<PaymentPromptProps> = ({
  onAddPayment,
  isLoading = false,
  className,
}) => {
  const { isSidebar, webAppUrl } = usePlatform();
  const { refreshTokens } = useAuth();

  const handleAddPayment = async () => {
    // If in sidebar (extension), open web app with subscription flow
    if (isSidebar) {
      logger.info('📱 [PAYMENT-PROMPT] Opening subscription in full web app');
      window.open(webAppUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    // Normal web app flow
    if (onAddPayment) {
      try {
        await onAddPayment();
      } catch (error) {
        logger.error('Add payment failed:', error);
      }
    }
  };
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refreshTokens();
      location.reload();
    } finally {
      setIsRefreshing(false);
    }
  };

  return (
    <div
      className={cn('flex flex-col items-center justify-center overflow-y-auto px-4', className)}
    >
      {/* Rocket Icon at the top */}
      <div className="mb-4 mt-6 md:mb-6 md:mt-12">
        <img
          src={BlueRocketSvg}
          alt="Rocket"
          className="w-20 h-20 md:w-24 md:h-24 xl:w-32 xl:h-32"
        />
      </div>

      {/* Main Title */}
      <h1 className="text-lg md:text-xl text-text-one mb-3 md:mb-4 text-center">
        Subscribe to CTRL <span className="font-bold">ENTER</span> Pro
      </h1>

      {/* Description */}
      <p className="text-sm md:text-base text-foreground mb-4 md:mb-6 text-center max-w-md leading-relaxed">
        {isSidebar
          ? 'Subscription setup requires the full web application for secure payment processing.'
          : 'Start your 7-day free trial today. Cancel anytime.'}
      </p>

      {/* Main Card */}
      <Card className="w-full max-w-[630px] p-4 md:p-6 xl:p-8 glass-card-bg glass-card-border border border-border-2 relative">
        {/* Card Title */}
        <h3 className="text-base md:text-lg font-bold text-foreground mb-3 md:mb-4 text-left">
          With your subscription you get:
        </h3>

        {/* Features List */}
        <ul className="space-y-2 md:space-y-3 text-xs md:text-sm mb-4 md:mb-6">
          <li className="flex items-start text-foreground">
            <img
              src={GreenCheckSvg}
              alt="Check"
              className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0 mt-0.5"
            />
            <div>
              <span>Error-checking for front desk data entry</span>
              <br />
              <span>(save $2k+ per month in mistakes)</span>
            </div>
          </li>
          <li className="flex items-center text-foreground">
            <img
              src={GreenCheckSvg}
              alt="Check"
              className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0"
            />
            <span>Instant prior authorization packages</span>
          </li>
          <li className="flex items-center text-foreground">
            <img
              src={GreenCheckSvg}
              alt="Check"
              className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0"
            />
            <span>Smarter coding suggestions for providers</span>
          </li>
          <li className="flex items-center text-foreground">
            <img
              src={GreenCheckSvg}
              alt="Check"
              className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0"
            />
            <span>Real-time seat-based billing tied to your team</span>
          </li>
        </ul>

        {/* Pricing Info */}
        <div className="mb-4 md:mb-6 text-xs md:text-sm">
          <p className="text-foreground">
            <span className="font-bold">Pricing:</span> per seat / month or year (billed
            automatically via Stripe)
          </p>
          <p className="text-foreground">
            Seats are managed by your Admin in the dashboard—add or remove at any time.
          </p>
        </div>

        {/* Subscribe Button */}
        <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4">
          {isSidebar ? (
            <Button
              onClick={handleAddPayment}
              variant="primary"
              size="lg"
              className="w-full sm:w-auto"
            >
              Open in Web App
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                />
              </svg>
            </Button>
          ) : (
            <>
              <Button
                onClick={handleAddPayment}
                disabled={isLoading}
                variant="primary"
                size="lg"
                className="w-full sm:w-auto"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Setting up...
                  </>
                ) : (
                  'Subscribe'
                )}
              </Button>

              <Button
                onClick={handleRefresh}
                disabled={isRefreshing}
                variant={'ghost'}
                className="w-full sm:w-auto h-10 px-3 rounded-md text-sm md:text-base font-medium text-text-secondary"
                size="lg"
              >
                {isRefreshing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                    Refreshing...
                  </>
                ) : (
                  'Refresh'
                )}
              </Button>
            </>
          )}
        </div>
      </Card>

      {/* Footer text under card */}
      <p className="text-xs text-bg-neutral-6 mt-4 md:mt-8 text-center mb-4 md:mb-8">
        Secure payment processing through Stripe
      </p>
    </div>
  );
};

PaymentPrompt.displayName = 'PaymentPrompt';
